import DailyIframe from "@daily-co/daily-js";
import type { DailyCall } from "@daily-co/daily-js";

export interface CallOptions {
  roomUrl: string;
  token?: string; // Daily.co meeting token
  userName?: string;
  startVideoOff?: boolean;
  startAudioOff?: boolean;
  container?: HTMLElement | null;
}

export class DailyService {
  private callFrame: DailyCall | null = null;
  private eventHandlers: Map<string, Function> = new Map();
  private joinPromise: Promise<DailyCall> | null = null;
  private pendingLeaveTimer: ReturnType<typeof setTimeout> | null = null;
  private currentRoomUrl: string | null = null;

  async joinCall(options: CallOptions): Promise<DailyCall> {
    // CRITICAL: Check if there's a pending leave and cancel it!
    if (this.pendingLeaveTimer) {
      console.log("[Daily] Canceling pending leave due to new join request");
      clearTimeout(this.pendingLeaveTimer);
      this.pendingLeaveTimer = null;
    }

    // 1. Check if we are already joining the SAME room
    if (this.joinPromise && this.currentRoomUrl === options.roomUrl) {
      console.log("[Daily] Join already in progress for this room, returning existing promise");
      // CRITICAL: Ensure we reparent the iframe when the promise resolves!
      return this.joinPromise.then((frame) => {
        if (options.container) {
          try {
            const iframe = (frame as any).iframe?.() as HTMLIFrameElement | null;
            if (iframe && !options.container.contains(iframe)) {
              console.log("[Daily] Reparenting existing iframe (from promise) to new container");
              options.container.appendChild(iframe);
            }
          } catch (err) {
            console.warn("[Daily] Error checking/reparenting iframe (promise):", err);
          }
        }
        return frame;
      });
    }

    // 2. Check if we are already joined to the SAME room
    if (this.callFrame && this.currentRoomUrl === options.roomUrl) {
      const state = this.callFrame.meetingState();
      if (state !== "left-meeting" && state !== "error") {
        console.log("[Daily] Already joined this room, returning existing frame");

        // CRITICAL: Reparent iframe if needed!
        if (options.container) {
          try {
            const iframe = (this.callFrame as any).iframe?.() as HTMLIFrameElement | null;
            if (iframe && !options.container.contains(iframe)) {
              console.log("[Daily] Reparenting existing iframe to new container");
              options.container.appendChild(iframe);
            }
          } catch (err) {
            console.warn("[Daily] Error checking/reparenting iframe:", err);
          }
        }

        // Ensure audio is enabled just in case
        try {
          this.callFrame.setLocalAudio(true);
        } catch (e) {
          // ignore
        }
        return this.callFrame;
      }
    }

    // 3. Otherwise, start a new join
    console.log("[Daily] Starting new join call flow");

    // Ensure instant cleanup of any previous session that wasn't for this room
    if (this.callFrame) {
      console.log("[Daily] Cleaning up previous session (immediate)");
      // Since we are here, it means we are switching rooms or the old session was bad
      // So we do a hard cleanup
      this.cleanup();
    }

    this.currentRoomUrl = options.roomUrl;

    // Create a new promise for this join attempt
    this.joinPromise = this._internalJoinCall(options)
      .then((frame) => {
        // Success: keep joinPromise null but keep currentRoomUrl
        this.joinPromise = null;
        return frame;
      })
      .catch((err) => {
        // Failure: clear everything
        this.joinPromise = null;
        this.currentRoomUrl = null;
        throw err;
      });

    return this.joinPromise;
  }

  private async _internalJoinCall(options: CallOptions): Promise<DailyCall> {
    console.log("[Daily] _internalJoinCall called with options:", {
      roomUrl: options.roomUrl,
      hasToken: !!options.token,
      userName: options.userName,
      startVideoOff: options.startVideoOff,
      startAudioOff: options.startAudioOff,
      hasContainer: !!options.container,
    });

    // CRITICAL: Clean up any existing call frame before creating a new one
    // Only do this if we determined (in joinCall) that we aren't reusing it
    if (this.callFrame) {
      console.log("[Daily] Cleaning up existing call frame before creating new one");
      try {
        await this.callFrame.leave().catch(() => null);
        await this.callFrame.destroy().catch(() => null);
        console.log("[Daily] ✓ Existing call frame cleaned up");
      } catch (cleanupErr) {
        console.warn("[Daily] Error cleaning up existing frame:", cleanupErr);
      }
      this.callFrame = null;
      // Give cleanup a moment to complete
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    // Also check for any existing Daily.co instances using getCallInstance
    try {
      const existingInstance = (DailyIframe as any).getCallInstance?.();
      if (existingInstance) {
        console.log("[Daily] Found existing Daily.co instance, destroying it...");
        try {
          // Check if it matches our room, though we shouldn't be here if it did (caught in joinCall)
          await existingInstance.leave().catch(() => null);
          await existingInstance.destroy().catch(() => null);
          console.log("[Daily] ✓ Existing instance destroyed");
        } catch (destroyErr) {
          console.warn("[Daily] Error destroying existing instance:", destroyErr);
        }
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    } catch (checkErr) {
      // getCallInstance might not be available, that's okay
      console.log("[Daily] Could not check for existing instances (this is okay)");
    }

    try {
      if (!options.container) {
        console.error(
          "[Daily] CRITICAL: joinCall was called without a container! This will cause a full-screen iframe. Not creating frame. Please provide a container element in the options."
        );
        this.callFrame = null;
        throw new Error("DailyService: joinCall requires a container element.");
      }

      const { container, ...callOptions } = options;

      // Create call frame
      const frameOptions = {
        iframeStyle: {
          position: "absolute",
          top: "0",
          left: "0",
          width: "100%",
          height: "100%",
          border: "0",
          borderRadius: "16px",
        },
        showLeaveButton: false,
        showFullscreenButton: false,
        showParticipantsBar: false,
        skipPrejoinUi: true,
        subscribeToTracksAutomatically: true,
        // Force Daily.co to skip prejoin and join immediately
        prejoin: false,
      };

      console.log("[Daily] Creating Daily.co frame...");
      this.callFrame = container
        ? DailyIframe.createFrame(container, frameOptions)
        : DailyIframe.createFrame(frameOptions);

      console.log("[Daily] ✓ Daily.co frame created:", {
        hasFrame: !!this.callFrame,
        hasContainer: !!container,
      });

      // Wait for iframe to be ready if container is provided
      if (container && this.callFrame) {
        console.log("[Daily] Waiting for iframe to load...");

        // Wait for iframe load event
        let iframeElement = container.querySelector("iframe");
        if (iframeElement) {
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error("Iframe load timeout after 10 seconds"));
            }, 10000);

            iframeElement!.onload = () => {
              clearTimeout(timeout);
              console.log("[Daily] ✓ Iframe loaded successfully");
              // Give it a moment after load to be fully ready
              setTimeout(() => resolve(), 500);
            };

            // If iframe is already loaded
            if (iframeElement!.contentDocument?.readyState === "complete") {
              clearTimeout(timeout);
              console.log("[Daily] ✓ Iframe already loaded");
              setTimeout(() => resolve(), 500);
            }
          });
        } else {
          // Fallback: wait a bit if we can't find the iframe
          console.log("[Daily] Iframe element not found, waiting 1 second...");
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }

        console.log("[Daily] ✓ Iframe should be ready now");
      }

      // Join the call with token if provided
      // Use the room URL as-is - prejoin is controlled by frame options
      const roomUrlWithPrejoin = callOptions.roomUrl;

      const joinOptions: any = {
        url: roomUrlWithPrejoin,
        userName: callOptions.userName,
        startVideoOff: callOptions.startVideoOff,
        startAudioOff: false, // CRITICAL: Always start with audio ON
        subscribeToTracksAutomatically: true,
      };

      console.log("[Daily] Room URL with prejoin=false:", roomUrlWithPrejoin);

      // Add token if provided (Daily.co requires token in join options, not URL)
      if (callOptions.token) {
        joinOptions.token = callOptions.token;
        console.log("[Daily] Token added to join options");

        // CRITICAL DEBUG: Decode JWT to see room_name
        try {
          const payload = JSON.parse(atob(callOptions.token.split(".")[1]));
          console.log("[Daily] 🔑 Token payload (decoded):", {
            room_name: payload.r, // Daily.co uses 'r' for room_name in token
            user_id: payload.u, // 'u' for user_id
            user_name: payload.un, // 'un' for user_name
            is_owner: payload.o, // 'o' for is_owner
            exp: payload.exp,
            rawPayload: payload,
          });
        } catch (e) {
          console.log("[Daily] Could not decode token:", e);
        }
      } else {
        console.warn("[Daily] No token provided for join");
      }

      console.log("[Daily] 🔵 Calling callFrame.join() with options:", {
        url: joinOptions.url,
        userName: joinOptions.userName,
        startVideoOff: joinOptions.startVideoOff,
        startAudioOff: joinOptions.startAudioOff,
        hasToken: !!joinOptions.token,
        tokenPreview: joinOptions.token ? `${joinOptions.token.substring(0, 20)}...` : "none",
      });

      // Set up event listeners BEFORE joining to catch join completion
      let joinCompleted = false;
      let joinErrorOccurred: Error | null = null;

      const joinedHandler = () => {
        console.log("[Daily] ✓✓✓ joined-meeting event fired - join completed!");
        joinCompleted = true;

        // Log room info after joining
        if (this.callFrame) {
          const meetingState = this.callFrame.meetingState();
          const participants = this.callFrame.participants();
          const roomInfo = (this.callFrame as any).room();
          console.log("[Daily] 🔵 Joined room state:", {
            meetingState,
            roomName: roomInfo?.name,
            roomUrl: roomInfo?.url || joinOptions.url,
            participantsCount: Object.keys(participants).length,
            participantIds: Object.keys(participants),
            participants: Object.values(participants).map((p: any) => ({
              sessionId: p.session_id,
              local: p.local,
              userName: p.user_name,
              userId: p.user_id,
              hasAudioTrack: !!p.audioTrack,
              hasVideoTrack: !!p.videoTrack,
              audio: p.audio,
              video: p.video,
            })),
          });

          // Subscribe to all existing remote participants
          Object.values(participants).forEach((p: any) => {
            if (!p.local) {
              try {
                this.callFrame?.updateParticipant(p.session_id, {
                  setSubscribedTracks: { audio: true, video: true },
                });
                console.log(`[Daily] ✓ Subscribed to existing remote participant: ${p.session_id}`);
              } catch (subErr) {
                console.warn(`[Daily] Could not subscribe to existing participant:`, subErr);
              }
            }
          });
        }
      };

      const errorHandler = (error: any) => {
        console.error("[Daily] Daily.co error event during join:", error);
        joinErrorOccurred = error instanceof Error ? error : new Error(String(error));
      };

      if (this.callFrame) {
        this.callFrame.on("joined-meeting", joinedHandler);
        this.callFrame.on("error", errorHandler);
        this.callFrame.on("participant-joined", (event: any) => {
          const participant = event.participant;
          const allParticipants = this.callFrame?.participants() || {};
          const roomInfo = (this.callFrame as any)?.room();

          console.log("[Daily] 🔔🔔🔔 participant-joined event fired:", {
            sessionId: participant?.session_id,
            local: participant?.local,
            userName: (participant as any)?.user_name,
            userId: (participant as any)?.user_id,
            hasAudioTrack: !!participant?.audioTrack,
            hasVideoTrack: !!participant?.videoTrack,
            audio: participant?.audio,
            video: participant?.video,
            allParticipants: Object.keys(allParticipants),
            roomUrl: roomInfo?.url || joinOptions.url,
            roomName: roomInfo?.name,
          });

          // Subscribe to remote participant tracks
          if (!participant?.local && this.callFrame) {
            try {
              this.callFrame.updateParticipant(participant.session_id, {
                setSubscribedTracks: { audio: true, video: true },
              });
              console.log(
                `[Daily] ✓✓✓ Subscribed to remote participant tracks: ${participant.session_id}`
              );
            } catch (subErr) {
              console.warn(`[Daily] Could not subscribe to remote participant tracks:`, subErr);
            }
          }
        });
        this.callFrame.on("participant-updated", (event: any) => {
          console.log("[Daily] participant-updated event:", event);
        });
      }

      // Start the join process (don't await the promise yet)
      console.log("[Daily] Starting join process...");
      const joinPromise = this.callFrame.join(joinOptions).catch((err) => {
        console.warn("[Daily] Join promise rejected (but may still succeed via event):", err);
        // Don't throw here - let the event handler or state check determine success
      });

      // Wait for either the promise, event, OR check meeting state periodically
      const joinWaitPromise = new Promise<void>((resolve, reject) => {
        let resolved = false;
        const timeout = setTimeout(() => {
          if (resolved) return;

          // Final check: see if we're actually in the meeting
          try {
            const participants = this.callFrame?.participants();
            const meetingState = this.callFrame?.meetingState();
            console.log(
              "[Daily] Timeout check - meeting state:",
              meetingState,
              "participants:",
              participants
            );

            if (
              meetingState === "joined-meeting" ||
              (participants && Object.keys(participants).length > 0)
            ) {
              console.log("[Daily] ✓ Actually in meeting despite timeout, resolving");
              resolved = true;
              resolve();
              return;
            }
          } catch (stateErr) {
            console.warn("[Daily] Could not check meeting state:", stateErr);
          }

          if (joinCompleted) {
            console.log("[Daily] Join completed via event, resolving");
            resolved = true;
            resolve();
          } else if (joinErrorOccurred) {
            console.error("[Daily] Join failed with error event");
            resolved = true;
            reject(joinErrorOccurred);
          } else {
            resolved = true;
            reject(
              new Error(
                "Daily.co join timeout after 15 seconds - no event fired and not in meeting"
              )
            );
          }
        }, 15000); // Increased timeout to 15 seconds

        // Check periodically if join completed or if we're in the meeting
        const checkInterval = setInterval(() => {
          if (resolved) {
            clearInterval(checkInterval);
            return;
          }

          if (joinCompleted) {
            clearTimeout(timeout);
            clearInterval(checkInterval);
            resolved = true;
            resolve();
            return;
          }

          if (joinErrorOccurred) {
            clearTimeout(timeout);
            clearInterval(checkInterval);
            resolved = true;
            reject(joinErrorOccurred);
            return;
          }

          // Check if we're actually in the meeting
          // Only accept "joined-meeting" state - don't accept "joining-meeting" as it means stuck
          try {
            const meetingState = this.callFrame?.meetingState();
            if (meetingState === "joined-meeting") {
              console.log("[Daily] ✓ Meeting state acceptable, resolving. State:", meetingState);
              const participants = this.callFrame?.participants();

              // Log room info even if event didn't fire
              if (this.callFrame) {
                const roomInfo = (this.callFrame as any).room();
                console.log("[Daily] 🔵 Room state (from state check):", {
                  meetingState,
                  roomName: roomInfo?.name,
                  roomUrl: roomInfo?.url || joinOptions.url,
                  participantsCount: Object.keys(participants || {}).length,
                  participantIds: Object.keys(participants || {}),
                  participants: Object.values(participants || {}).map((p: any) => ({
                    sessionId: p.session_id,
                    local: p.local,
                    userName: p.user_name,
                    userId: p.user_id,
                    hasAudioTrack: !!p.audioTrack,
                    hasVideoTrack: !!p.videoTrack,
                    audio: p.audio,
                    video: p.video,
                  })),
                });

                // Subscribe to all existing remote participants
                Object.values(participants || {}).forEach((p: any) => {
                  if (!p.local) {
                    try {
                      this.callFrame?.updateParticipant(p.session_id, {
                        setSubscribedTracks: { audio: true, video: true },
                      });
                      console.log(
                        `[Daily] ✓ Subscribed to existing remote participant (state check): ${p.session_id}`
                      );
                    } catch (subErr) {
                      console.warn(
                        `[Daily] Could not subscribe to existing participant (state check):`,
                        subErr
                      );
                    }
                  }
                });
              }

              clearTimeout(timeout);
              clearInterval(checkInterval);
              resolved = true;
              resolve();
            }
          } catch (stateErr) {
            // Ignore state check errors
          }
        }, 500); // Check every 500ms
      });

      try {
        // Race between promise and our wait promise
        await Promise.race([joinPromise, joinWaitPromise]);
        console.log("[Daily] ✓✓✓ Join completed (via promise, event, or state check)");
      } catch (joinError) {
        // Final check: are we actually in the meeting?
        try {
          const meetingState = this.callFrame?.meetingState();
          const participants = this.callFrame?.participants();
          if (
            meetingState === "joined-meeting" ||
            (participants && Object.keys(participants).length > 0)
          ) {
            console.log("[Daily] ✓ Actually in meeting despite error, continuing");
            // Don't throw - we're actually joined
          } else if (joinCompleted) {
            console.log("[Daily] Join completed via event, ignoring error");
            // Don't throw - event said we joined
          } else if (joinErrorOccurred) {
            console.error("[Daily] ✗✗✗ Join failed with error event:", joinErrorOccurred);
            throw joinErrorOccurred;
          } else {
            console.error("[Daily] ✗✗✗ callFrame.join() failed or timed out:", {
              error: joinError,
              errorMessage: joinError instanceof Error ? joinError.message : String(joinError),
              errorStack: joinError instanceof Error ? joinError.stack : undefined,
              joinCompleted,
            });
            throw joinError;
          }
        } catch (finalCheckErr) {
          // If final check fails, throw original error
          throw joinError;
        }
      }

      // Don't remove event listeners - we need them for the call
      // They'll be cleaned up when the frame is destroyed

      // CRITICAL: Ensure audio is enabled after joining
      // Do this multiple times to ensure it sticks
      try {
        await this.callFrame.setLocalAudio(true);
        console.log("[Daily] ✓ Local audio enabled after join");

        // Double-check audio state
        const audioState = this.callFrame.localAudio();
        console.log("[Daily] Local audio state:", audioState);

        if (audioState === false) {
          console.warn("[Daily] Audio was false, re-enabling...");
          await this.callFrame.setLocalAudio(true);
        }

        // Retry after a short delay to ensure it sticks
        setTimeout(async () => {
          try {
            if (this.callFrame) {
              // Check if iframe is still valid before trying to enable audio
              const iframe = this.callFrame.iframe();
              if (iframe && iframe.contentWindow) {
                await this.callFrame.setLocalAudio(true);
                const audioStateAfter = this.callFrame.localAudio();
                console.log("[Daily] Re-confirmed local audio enabled, state:", audioStateAfter);
              } else {
                console.warn("[Daily] Iframe no longer valid, skipping audio retry");
              }
            }
          } catch (retryErr) {
            console.warn("[Daily] Could not enable audio on retry:", retryErr);
          }
        }, 1000);
      } catch (audioErr) {
        console.error("[Daily] Could not enable audio:", audioErr);
      }

      console.log("[Daily] Joined call:", callOptions.roomUrl);

      // Set up periodic participant check to catch late joiners
      if (this.callFrame) {
        const participantCheckInterval = setInterval(() => {
          if (!this.callFrame) {
            clearInterval(participantCheckInterval);
            return;
          }

          try {
            const participants = this.callFrame.participants();
            const participantCount = Object.keys(participants).length;
            const remoteCount = Object.values(participants).filter((p: any) => !p.local).length;

            if (participantCount > 1 && remoteCount > 0) {
              console.log("[Daily] Periodic check: Found remote participants:", {
                total: participantCount,
                remote: remoteCount,
                participantIds: Object.keys(participants),
              });

              // Subscribe to any remote participants we haven't subscribed to yet
              Object.values(participants).forEach((p: any) => {
                if (!p.local) {
                  try {
                    this.callFrame?.updateParticipant(p.session_id, {
                      setSubscribedTracks: { audio: true, video: true },
                    });
                    console.log(
                      `[Daily] Periodic check: Subscribed to remote participant: ${p.session_id}`
                    );
                  } catch (subErr) {
                    console.warn(`[Daily] Periodic check: Could not subscribe:`, subErr);
                  }
                }
              });
            }
          } catch (checkErr) {
            console.warn("[Daily] Error in periodic participant check:", checkErr);
          }
        }, 3000); // Check every 3 seconds

        // Store interval for cleanup
        (this.callFrame as any)._participantCheckInterval = participantCheckInterval;
      }

      return this.callFrame;
    } catch (error) {
      console.error("[Daily] Failed to join call:", error);
      this.cleanup();
      throw error;
    }
  }

  async leaveCall(): Promise<void> {
    if (this.joinPromise) {
      // If currently joining, wait for it to finish then leave
      try {
        await this.joinPromise;
        // No await frame.leave() here, let soft leave handle it
        this.joinPromise = null;
        this.currentRoomUrl = null;
      } catch (e) {
        // Ignore
      }
    }

    if (!this.callFrame) {
      // Already gone
      return;
    }

    console.log("[Daily] Requesting soft leave (debounced 200ms)...");

    // Clear any existing timer to restart debounce? Or just let it run?
    // Restarting it defers it further.
    if (this.pendingLeaveTimer) {
      clearTimeout(this.pendingLeaveTimer);
    }

    return new Promise((resolve) => {
      this.pendingLeaveTimer = setTimeout(async () => {
        console.log("[Daily] Executing hard leave now");
        this.pendingLeaveTimer = null;

        try {
          if (this.callFrame) {
            // Check if iframe is still valid
            const iframe = (this.callFrame as any).iframe?.();
            if (iframe) {
              await this.callFrame.leave().catch(() => {});
            }
            this.cleanup(); // Destroys frame
          }
        } catch (err) {
          console.warn("[Daily] Error during hard leave:", err);
        } finally {
          this.callFrame = null;
          this.currentRoomUrl = null;
          resolve();
        }
      }, 200);
    });
  }

  async toggleCamera(): Promise<void> {
    if (!this.callFrame) return;

    try {
      const videoState = this.callFrame.localVideo();
      const isEnabled = videoState !== false;
      await this.callFrame.setLocalVideo(!isEnabled);
    } catch (error) {
      console.error("[Daily] Error toggling camera:", error);
    }
  }

  async toggleMicrophone(): Promise<void> {
    if (!this.callFrame) return;

    try {
      const audioState = this.callFrame.localAudio();
      const isEnabled = audioState !== false;
      await this.callFrame.setLocalAudio(!isEnabled);
    } catch (error) {
      console.error("[Daily] Error toggling microphone:", error);
    }
  }

  async startScreenShare(): Promise<void> {
    if (!this.callFrame) return;

    try {
      await this.callFrame.startScreenShare();
    } catch (error) {
      console.error("[Daily] Error starting screen share:", error);
    }
  }

  async stopScreenShare(): Promise<void> {
    if (!this.callFrame) return;

    try {
      await this.callFrame.stopScreenShare();
    } catch (error) {
      console.error("[Daily] Error stopping screen share:", error);
    }
  }

  on(event: string, handler: Function): void {
    if (!this.callFrame) return;

    this.eventHandlers.set(event, handler);
    this.callFrame.on(event as any, handler as any);
  }

  off(event: string): void {
    if (!this.callFrame) return;

    const handler = this.eventHandlers.get(event);
    if (handler) {
      this.callFrame.off(event as any, handler as any);
      this.eventHandlers.delete(event);
    }
  }

  getCallFrame(): DailyCall | null {
    return this.callFrame;
  }

  isInCall(): boolean {
    return this.callFrame !== null;
  }

  private cleanup(): void {
    if (this.callFrame) {
      try {
        // Clear participant check interval if it exists
        const interval = (this.callFrame as any)._participantCheckInterval;
        if (interval) {
          clearInterval(interval);
          console.log("[Daily] Cleared participant check interval");
        }

        // Remove all event handlers
        this.eventHandlers.forEach((handler, event) => {
          try {
            this.callFrame?.off(event as any, handler as any);
          } catch (err) {
            // Ignore errors removing event handlers
          }
        });
        this.eventHandlers.clear();

        // Destroy the frame (only if it's still valid)
        // Check if the iframe still exists in the DOM before destroying
        try {
          const iframe = this.callFrame.iframe();
          if (iframe && iframe.contentWindow) {
            this.callFrame.destroy().catch((err) => {
              // Ignore errors if iframe is already destroyed
              console.warn("[Daily] Error destroying frame (ignored):", err);
            });
          } else {
            console.warn("[Daily] Iframe already removed from DOM, skipping destroy");
          }
        } catch (err) {
          // Ignore errors if iframe() method fails
          console.warn("[Daily] Error checking iframe (ignored):", err);
        }
      } catch (error) {
        // Ignore errors during cleanup - iframe might already be removed
        console.warn("[Daily] Error during cleanup (ignored):", error);
      } finally {
        this.callFrame = null;
      }
    }
  }
}

// Create singleton instance
export const dailyService = new DailyService();

export default dailyService;
